import { decodeMqttText } from '@/contexts/mqtt/payload';

import type { LocationVideoCage, LocationVideoState } from './locationVideoStore';

type VideoMessageActions = Pick<
    LocationVideoState,
    | 'updateBioscopeOnlineStatus'
    | 'updateMuxData'
    | 'buttonActionHandler'
    | 'setOnlyViewMode'
    | 'updateBioscopeData'
>;

interface VideoMessageContext extends VideoMessageActions {
    userId: number | null;
    /** The pens as they are now; the handler is registered once and outlives any snapshot. */
    getActiveCages: () => LocationVideoCage[];
}

/** Whether a pen other than this user's own is streaming, by the mux state the page holds. */
export function isAnotherUserStreaming(cages: LocationVideoCage[], userId: number | null): boolean {
    return cages.some(
        (cage) =>
            cage.muxData?.userId != null &&
            cage.muxData.userId !== userId &&
            cage.muxData.status !== 'idle'
    );
}

function extractBioscopeIdFromTopic(topic: string): number | null {
    const match = topic.match(/(?:status|livestream|response)\/(\d+)(?:\/status|$)/);
    return match ? Number.parseInt(match[1], 10) : null;
}

/**
 * Applies the bioscope status, livestream and sensor data topics to the location video store.
 */
export function createVideoMessageHandler(
    context: VideoMessageContext
): (topic: string, message: Uint8Array) => Promise<void> {
    const {
        userId: user_id,
        getActiveCages,
        updateBioscopeOnlineStatus,
        updateMuxData,
        buttonActionHandler,
        setOnlyViewMode,
        updateBioscopeData,
    } = context;

    return async (topic: string, message: Uint8Array) => {
        if (topic.startsWith('bioscope/status')) {
            try {
                const parsedMessage = JSON.parse(decodeMqttText(message));
                updateBioscopeOnlineStatus(parsedMessage.bioscopeId, parsedMessage.status);
            } catch (error) {
                // console.error('Error parsing MQTT message:', error);
            }
        }

        if (topic.startsWith('bioscope/livestream')) {
            try {
                const parsedMessage = JSON.parse(decodeMqttText(message));
                const bioscopeId = extractBioscopeIdFromTopic(topic);

                updateMuxData(bioscopeId, {
                    streamId: parsedMessage.streamId,
                    status: parsedMessage.status,
                    timestamp: parsedMessage.timestamp,
                    userId: parsedMessage.startedByUserId || null,
                    playbackId: parsedMessage.playbackId || null,
                });

                if (parsedMessage.status === 'disconnected') {
                    buttonActionHandler(bioscopeId, false);
                }

                if (parsedMessage.startedByUserId !== user_id) {
                    setOnlyViewMode(true);
                }
                if (parsedMessage.startedByUserId === null) {
                    setOnlyViewMode(isAnotherUserStreaming(getActiveCages(), user_id));
                }
            } catch (error) {
                // console.error('Error parsing MQTT message:', error);
            }
        }

        if (topic.startsWith('bioscope/data/response')) {
            try {
                const parsedMessage = JSON.parse(decodeMqttText(message));
                const bioscopeId = extractBioscopeIdFromTopic(topic);

                updateBioscopeData(bioscopeId, {
                    roll: parsedMessage.Roll,
                    pitch: parsedMessage.Pitch,
                    depth: parsedMessage.Depth,
                    waterTemperature: parsedMessage['Water Temperature'],
                });
            } catch (error) {
                console.error('Error parsing MQTT message:', error);
            }
        }
    };
}
